import { logger } from '../services/logger';
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  verifyBeforeUpdateEmail
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./config";
import { getActiveTenantId } from "./tenant";
import {
  applyAuthPersistence,
  provisionUserAccount
} from "./auth/profile";

// Re-export all modular auth subcomponents (tokens, roles, profile, session)
export * from "./auth";

// Initialize Recaptcha (Required for Phone Auth on Web)
export const setupRecaptcha = (elementId) => {
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (e) {
      logger.warn("Could not clear existing recaptchaVerifier:", e);
    }
    window.recaptchaVerifier = null;
  }

  window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
    'size': 'invisible',
    'callback': () => {
      // reCAPTCHA solved
    },
    'expired-callback': () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch {
          // ignore clear error
        }
        window.recaptchaVerifier = null;
      }
    }
  });

  return window.recaptchaVerifier;
};

export const sendPhoneCode = async (phoneNumber, appVerifier, keepLoggedIn = false) => {
  await applyAuthPersistence(keepLoggedIn);
  try {
    // Web Phone Auth
    logger.info("Attempting to send code to:", phoneNumber);
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    return confirmationResult; // Contains .confirm(code) method
  } catch (error) {
    logger.error("Error in sendPhoneCode:", error);
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch {
        // ignore clear error
      }
      window.recaptchaVerifier = null;
    }
    throw error;
  }
};

// Email Login: Send Magic Link
export const sendEmailLink = async (email, continueUrl, keepLoggedIn = false) => {
  let targetUrl = continueUrl || window.location.href;
  if (keepLoggedIn) {
    try {
      const urlObj = new URL(targetUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      urlObj.searchParams.set('keepLoggedIn', 'true');
      targetUrl = urlObj.toString();
    } catch {
      const sep = targetUrl.includes('?') ? '&' : '?';
      targetUrl = `${targetUrl}${sep}keepLoggedIn=true`;
    }
  }

  try {
    const sendLinkFn = httpsCallable(functions, 'sendSignInLink');
    await sendLinkFn({ email, continueUrl: targetUrl, tenantId: getActiveTenantId() });
  } catch (err) {
    logger.warn("sendSignInLink Cloud Function failed, falling back to Firebase Auth sendSignInLinkToEmail:", err);
    const actionCodeSettings = {
      url: targetUrl,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  }
  await applyAuthPersistence(keepLoggedIn);
  window.localStorage.setItem('emailForSignIn', email);
  window.localStorage.setItem('pendingKeepLoggedIn', keepLoggedIn ? 'true' : 'false');
  return true;
};

// Request Email Verification (via SES with custom domain /auth/action link, falling back to verifyBeforeUpdateEmail)
export const requestEmailVerification = async (user, email, continueUrl) => {
  const targetUrl = continueUrl || window.location.href;
  try {
    const sendVerificationFn = httpsCallable(functions, 'sendEmailVerification');
    await sendVerificationFn({ email, continueUrl: targetUrl, tenantId: getActiveTenantId() });
    return true;
  } catch (err) {
    logger.warn("sendEmailVerification Cloud Function failed, falling back to Firebase Auth verifyBeforeUpdateEmail:", err);
    const actionCodeSettings = {
      url: targetUrl,
      handleCodeInApp: true,
    };
    await verifyBeforeUpdateEmail(user || auth.currentUser, email, actionCodeSettings);
    return true;
  }
};

// Pending Google credential storage for cross-session/cross-step linking (AUD-077)
let pendingGoogleCredential = null;
export const getPendingGoogleCredential = () => pendingGoogleCredential;
export const clearPendingGoogleCredential = () => { pendingGoogleCredential = null; };

// Check if URL contains email sign-in link parameters (Issue #655)
export const isEmailLoginLink = (url) => {
  if (typeof window === 'undefined') return false;
  const targetUrl = url || window.location.href;
  try {
    return isSignInWithEmailLink(auth, targetUrl);
  } catch {
    return false;
  }
};

const resolveEmailForLogin = () => {
  let email = window.localStorage.getItem('emailForSignIn');
  const params = new URLSearchParams(window.location.search);
  if (!email) {
    email = params.get('email');
  }
  if (!email) {
    email = window.prompt('Please provide your email for confirmation');
  }
  return email;
};

const finalizeEmailLoginUser = async (user) => {
  if (pendingGoogleCredential) {
    try {
      await linkWithCredential(user, pendingGoogleCredential);
      logger.info(`Successfully linked Google credential to ${user.email}`);
      pendingGoogleCredential = null;
    } catch (linkErr) {
      logger.warn("Failed auto-linking pending Google credential:", linkErr);
    }
  }
  try {
    if (typeof user.getIdToken === 'function') {
      await user.getIdToken(true);
    }
  } catch (tokErr) {
    logger.warn("Failed to force refresh ID token in completeEmailLogin:", tokErr);
  }
  if (user.emailVerified || user.phoneNumber) {
    await provisionUserAccount(user);
  }
};

// Complete Email Login (Check if link is valid and sign in)
export const completeEmailLogin = async () => {
  if (!isSignInWithEmailLink(auth, window.location.href)) {
    return null;
  }

  const email = resolveEmailForLogin();
  if (!email) {
    logger.warn("completeEmailLogin: No email address provided for sign-in confirmation.");
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const paramKeep = params.get('keepLoggedIn');
  const localKeep = window.localStorage.getItem('pendingKeepLoggedIn');
  const keepLoggedIn = paramKeep === 'true' || (paramKeep === null && localKeep === 'true');
  await applyAuthPersistence(keepLoggedIn);

  const result = await signInWithEmailLink(auth, email, window.location.href);
  window.localStorage.removeItem('emailForSignIn');
  window.localStorage.removeItem('pendingKeepLoggedIn');

  if (result?.user) {
    await finalizeEmailLoginUser(result.user);
  }
  return result?.user || null;
};

const linkAccountIfUserPresent = async (pendingCred) => {
  const linkResult = await linkWithCredential(auth.currentUser, pendingCred);
  if (linkResult?.user && (linkResult.user.emailVerified || linkResult.user.phoneNumber)) {
    try {
      if (typeof linkResult.user.getIdToken === 'function') {
        await linkResult.user.getIdToken(true);
      }
    } catch (tokErr) {
      logger.warn("Failed to force refresh ID token in linkWithCredential:", tokErr);
    }
    await provisionUserAccount(linkResult.user);
    pendingGoogleCredential = null;
    return linkResult.user;
  }
  return null;
};

const handleAccountExistsConflict = async (error) => {
  const email = error.customData?.email;
  const pendingCred = GoogleAuthProvider.credentialFromError(error);
  if (pendingCred) {
    pendingGoogleCredential = pendingCred;
  }
  if (!email || !pendingCred) return null;

  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    logger.warn(`Account exists for ${email} with methods: ${methods.join(', ')}`);
    if (auth.currentUser) {
      return await linkAccountIfUserPresent(pendingCred);
    }
    const customErr = new Error(
      `An account already exists for ${email} using ${methods.join(', ')}. Please sign in using your primary method to link your Google account.`
    );
    customErr.code = 'auth/account-exists-with-different-credential';
    customErr.email = email;
    customErr.methods = methods;
    throw customErr;
  } catch (linkErr) {
    if (linkErr.code === 'auth/account-exists-with-different-credential') throw linkErr;
    logger.error("Linking credential error:", linkErr);
    return null;
  }
};

// Google Sign In (AUD-077)
export const signInWithGoogle = async (keepLoggedIn = false) => {
  await applyAuthPersistence(keepLoggedIn);
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    if (result?.user) {
      try {
        if (typeof result.user.getIdToken === 'function') {
          await result.user.getIdToken(true);
        }
      } catch (tokErr) {
        logger.warn("Failed to force refresh ID token in signInWithGoogle:", tokErr);
      }
      if (result.user.emailVerified || result.user.phoneNumber) {
        await provisionUserAccount(result.user);
      }
      return result.user;
    }
    return result?.user;
  } catch (error) {
    if (error.code === 'auth/account-exists-with-different-credential') {
      const linkedUser = await handleAccountExistsConflict(error);
      if (linkedUser) return linkedUser;
    }
    throw error;
  }
};
